import {AssetLanguageLabels} from '@backstage/api-types/constants';
import {AttendeeModel} from '@backstage-components/attendee-container';
import axios from 'axios';
import type {
  HttpRequestType,
  SourceConfig,
  SourceLabelingConfig,
} from 'bitmovin-player';
import type {DrmConfig} from './Video.interfaces';

type LabeledLanguageCode = keyof typeof AssetLanguageLabels;

export type TGetTimeData = {
  utc: string;
};

/**
 * Returns the `.player-switch` element
 */
export const queryPlayerSwitch = (
  playerWrapper: HTMLElement
): HTMLElement | null =>
  playerWrapper.querySelector<HTMLElement>('.player-switch');

/**
 * Returns the video module wrapper class list
 */
export const generateWrapperClassName = (moduleId: string): string => {
  return `video-module-wrapper-${moduleId}`;
};

export const isJson = (str: string): boolean => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

export const sourceLabelingConfig: SourceLabelingConfig = {
  tracks: (data) => {
    const {lang, mimeType} = data;

    let processedLang: string;
    let modifier = '';

    // This function is called on video tracks as well as audio.
    // Video tracks do not have a language, so we return emptystring
    // to prevent the player from erroring and preventing playback.
    if (!lang || (mimeType && !mimeType.includes('audio'))) {
      return '';
    }

    if (lang.toLowerCase().endsWith('-ad')) {
      // Remove the last three characters from the language code string (`-ad`)
      processedLang = lang.slice(0, -3);
      modifier = ' [AD]';
    } else {
      processedLang = lang;
    }

    const rawLabel = isLabeledLanguageCode(processedLang)
      ? AssetLanguageLabels[processedLang as LabeledLanguageCode]?.label
      : lang;

    return `${rawLabel}${modifier}`;
  },
  subtitles: (data) => {
    const {lang} = data;
    let processedLang: string;
    let modifier = '';

    if (lang.toLowerCase().endsWith('-cc')) {
      // Remove the last three characters from the language code string (`-cc`)
      processedLang = lang.slice(0, -3);
      modifier = ' [CC]';
    } else {
      processedLang = lang;
    }
    const rawLabel = isLabeledLanguageCode(processedLang)
      ? AssetLanguageLabels[processedLang as LabeledLanguageCode]?.label
      : lang;

    return `${rawLabel}${modifier}`;
  },
};

/**
 * Checks if there is a display text mapping for the given language code.
 */
export function isLabeledLanguageCode(
  input: string
): input is LabeledLanguageCode {
  return input in AssetLanguageLabels;
}

/**
 * Duplicated from `bitmovin-player` `HttpRequestType` enum. The associated type
 * is imperfect but should at least require that each key is represented.
 */
export const BitmovinRequestType: Record<
  keyof typeof HttpRequestType,
  `${HttpRequestType}`
> = {
  MANIFEST_DASH: 'manifest/dash',
  MANIFEST_HLS_MASTER: 'manifest/hls/master',
  MANIFEST_HLS_VARIANT: 'manifest/hls/variant',
  MANIFEST_SMOOTH: 'manifest/smooth',
  MANIFEST_ADS: 'manifest/ads',
  MEDIA_AUDIO: 'media/audio',
  MEDIA_VIDEO: 'media/video',
  MEDIA_SUBTITLES: 'media/subtitles',
  MEDIA_THUMBNAILS: 'media/thumbnails',
  MEDIA_SEGMENTINDEX: 'media/segmentindex',
  DRM_LICENSE_WIDEVINE: 'drm/license/widevine',
  DRM_LICENSE_PLAYREADY: 'drm/license/playready',
  DRM_LICENSE_FAIRPLAY: 'drm/license/fairplay',
  DRM_LICENSE_PRIMETIME: 'drm/license/primetime',
  DRM_LICENSE_CLEARKEY: 'drm/license/clearkey',
  DRM_CERTIFICATE_FAIRPLAY: 'drm/certificate/fairplay',
  KEY_HLS_AES: 'key/hls/aes',
  TIME_SYNC: 'time/sync',
  UNKNOWN: 'unknown',
  /**
   * @hidden
   */
  INTERNAL: 'internal',
} as const;

/**
 * Returns a `SourceConfig` with functions to label the audio and subtitle
 * tracks and the DRM configuratio included. If the `DrmConfig` indicates DRM
 * is required but there is no `token` yet returns `undefined`.
 */
export const getMappedSource = (
  source: SourceConfig,
  drm: DrmConfig
): SourceConfig | undefined => {
  let mappedSource: SourceConfig = {
    ...source,
    labeling: {
      dash: sourceLabelingConfig,
      hls: sourceLabelingConfig,
    },
    metadata: {
      ...drm.config,
      scope: 'backstage',
    },
  };

  if (drm.isDrmRequired && drm.token) {
    mappedSource = {
      ...mappedSource,
      drm: {
        widevine: {
          LA_URL: 'https://multidrm.core.verimatrixcloud.net/widevine',
          headers: {
            Authorization: drm.token,
            'Content-Type': 'application/octet-stream',
          },
        },
        playready: {
          LA_URL: 'https://multidrm.core.verimatrixcloud.net/playready',
          headers: {
            Authorization: drm.token,
            'Content-Type': 'application/xml',
          },
        },
        fairplay: {
          certificateURL:
            'https://d2n3tysaajnxa7.cloudfront.net/fps_certificate.der',
          LA_URL: 'https://multidrm.core.verimatrixcloud.net/fairplay',
          headers: {
            'Content-Type': 'application/json',
            Authorization: drm.token,
          },
          prepareContentId: (contentId) => {
            return contentId.slice(8);
          },
          prepareMessage: (event) => {
            return JSON.stringify({
              spc: event.messageBase64Encoded,
            });
          },
          prepareLicense: (license) => {
            const {ckc} = JSON.parse(license);
            return ckc;
          },
          useUint16InitData: true,
        },
      },
    };
  } else if (drm.isDrmRequired && !drm.token) {
    return undefined;
  }
  return mappedSource;
};

export const decToBinary = (dec: number): string => (dec >>> 0).toString(2);

export const parseIntegerArray = <T>(integerArray: number[]): string | T => {
  const binaryArray = integerArray.map((val) => decToBinary(val));
  const str = binaryArray
    .map((bin) => String.fromCharCode(parseInt(bin, 2)))
    .join('');
  if (isJson(str)) {
    return JSON.parse(str);
  }
  return str;
};

export const hashString = (input: string): number => {
  let hash = 0;
  if (input.length === 0) return hash;
  for (let i = 0; i < input.length; i++) {
    // Get the current chracter code
    const char = input.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    // Convert to 32bit integer
    hash = hash & hash;
  }
  return hash;
};

export const timeSince = async (
  startTime: Date,
  useServerTime?: boolean,
  timeServiceURL?: string
): Promise<number> => {
  let curTime;
  if (useServerTime && typeof timeServiceURL === 'string') {
    const {data} = await axios.get<TGetTimeData>(timeServiceURL);
    curTime = data.utc ? new Date(data.utc) : new Date();
  } else {
    curTime = new Date();
  }

  return Math.round((curTime.getTime() - startTime.getTime()) / 1000);
};

export const fixSafariTabulation = (): void => {
  console.log(
    'Safari Tabulation Fix is active. Default Safari Tabulation settings will be overridden.'
  );
  // Tab object keeps track of incoming elements for tabulation (starts tabulation at 1)
  const Tab = {
    i: 1,
    items: 0,
    seekBar: 0,
    volumeBar: 0,
  };

  // Finds all button and select elements and enumerates Tab object. Conditionally find seekbar and volumebar and enumerate Tab object to keep tab flow organized
  Array.from(document.querySelectorAll('button, select')).forEach((element) => {
    // Does not include buttons that are hidden inside within bitmovin player's UI
    if (element.className.match('bmpui-hidden')) return;
    element.setAttribute('tabIndex', `${Tab.i}`);
    Tab.i++;
    Tab.items++;
    //
    if (
      element
        .getAttribute('class')
        ?.includes('bmpui-ui-hugeplaybacktogglebutton')
    ) {
      document
        .getElementsByClassName('bmpui-ui-seekbar')
        [Tab.seekBar]?.setAttribute('tabIndex', `${Tab.i}`);
      Tab.i++;
      Tab.items++;
      Tab.seekBar++;
    }
    if (
      element.getAttribute('class')?.includes('bmpui-ui-volumetogglebutton')
    ) {
      document
        .getElementsByClassName('bmpui-ui-volumeslider')
        [Tab.volumeBar]?.setAttribute('tabIndex', `${Tab.i}`);
      Tab.i++;
      Tab.items++;
      Tab.volumeBar++;
    }
  });

  // Finds all input elements and enumerates Tab object
  Array.from(document.getElementsByTagName('input')).forEach((element) => {
    // Does not include inputs that are hidden
    if (element?.type === 'hidden') return;
    element.setAttribute('tabIndex', `${Tab.i}`);
    Tab.i++;
    Tab.items++;
  });

  const incrementTabulation = (): void => {
    Tab.i = Tab.i !== Tab.items ? Tab.i + 1 : 1;
  };

  const decrementTabulation = (): void => {
    if (Tab.i === 0) {
      Tab.i === Tab.items;
    } else {
      Tab.i = Tab.i !== 1 ? Tab.i - 1 : Tab.items;
    }
  };

  const handleTabulation = (tabFn: () => void): void => {
    try {
      while (
        getComputedStyle(
          document.querySelector(
            `div[tabindex="${Tab.i}"], button[tabindex="${Tab.i}"], select[tabindex="${Tab.i}"], input[tabindex="${Tab.i}"]`
          ) as HTMLElement
        ).visibility === 'hidden'
      ) {
        tabFn();
      }
    } catch (err) {
      console.log(
        `You encountered an error with incrementing tabindex: ${err}`
      );
      return;
    }
  };

  // Set the index back to 0 for proper tabbing logic.
  Tab.i = 0;

  // Add eventListener to document to handle for Tab button keydown
  document.addEventListener('keydown', (e) => {
    // Handles for Tab (no shift)
    if (e.keyCode === 9 && !e.shiftKey) {
      e.preventDefault();

      // Increments Tab index, else resets back to beginning if end is reached
      incrementTabulation();
      // We check to see if the upcoming element is hidden, if so we increment again
      handleTabulation(incrementTabulation);
      // Selectively assign focus to the element at the specific tab index
      (
        document.querySelector(
          `div[tabindex="${Tab.i}"], button[tabindex="${Tab.i}"], select[tabindex="${Tab.i}"], input[tabindex="${Tab.i}"]`
        ) as HTMLElement | null
      )?.focus();
    }

    // Handles for Tab (with shift)
    if (e.keyCode === 9 && e.shiftKey) {
      e.preventDefault();
      // Handles for when user shift tabs as initial input (i.e. index is 0) - in this case, we just set index to the end. Otherwise, we will decrement tab index accordingly.
      decrementTabulation();
      // We check to see if the upcoming element is hidden, if so we decrement again
      handleTabulation(decrementTabulation);
      // Selectively assign focus to the element at the specific tab index

      (
        document.querySelector(
          `div[tabindex="${Tab.i}"], button[tabindex="${Tab.i}"], select[tabindex="${Tab.i}"], input[tabindex="${Tab.i}"]`
        ) as HTMLElement | null
      )?.focus();
    }
  });

  // Add eventListener to document to handle for mouse click
  document.addEventListener('click', () => {
    // If the document's active element ever has an attribute called tabIndex, we will assign Tab index to that index. otherwise, focus can be assumed to lost and we reset it to 0.
    if (document.activeElement?.getAttribute('tabIndex')) {
      Tab.i = Number(document.activeElement?.getAttribute('tabIndex'));
    } else {
      Tab.i = 0;
    }
  });
};

/**
 * Manually fetches the FairPlay DRM certificate. This is to resolve a bug in the bitmovin player
 * where sometimes on iOS, the player attempts to decrypt the content before the certificate
 * has been returned.
 * @returns The fairplay certificate in an ArrayBuffer
 */
export const fetchFairplayCertificate = async (): Promise<ArrayBuffer> => {
  const res = await fetch(
    'https://d2n3tysaajnxa7.cloudfront.net/fps_certificate.der'
  );
  return res.arrayBuffer();
};

/**
 * Generates watermark text based on a passed-in template and attendee.
 * @param {string} template - the template to use for the watermark text. %name% will be replaced with the attendee's name. %email% will be replaced with the attendee's email.
 * @param {AttendeeModel | null} attendee - the attendee to use for the watermark text.
 * @returns {string} the template string (with name and email modifications, if any).
 */
export const generateWatermark = (
  template: string,
  attendee?: AttendeeModel | null
): string => {
  if (!attendee) {
    return template;
  } else if (attendee.email === 'skipped-auth@example.com') {
    return 'No authentication, remove watermark';
  } else {
    return template
      .replace(/%name%/g, attendee.name)
      .replace(/%email%/g, attendee.email || '');
  }
};
